// 通用安全验证服务（2FA / Passkey 重试），与内部参考实现行为对齐
import {
  verifyApi,
  passkeyVerifyBeginApi,
  passkeyVerifyFinishApi,
  get2FAStatusSimpleApi,
  getPasskeyStatusSimpleApi,
} from '@/api/verify'

export interface VerificationMethods {
  has2FA: boolean
  hasPasskey: boolean
  passkeySupported: boolean
}

export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials
  )
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '=='.slice((base64.length + 2) % 4)
  const raw = atob(padded)
  const buffer = new ArrayBuffer(raw.length)
  const view = new Uint8Array(buffer)
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i)
  return buffer
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let str = ''
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i])
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function prepareCredentialRequestOptions(options: Record<string, unknown>) {
  const prepared: Record<string, unknown> = { ...options }
  if (typeof (options as { challenge?: string }).challenge === 'string') {
    prepared.challenge = base64urlToBuffer((options as { challenge: string }).challenge)
  }
  const allowCredentials = (options as { allowCredentials?: Array<{ id: string; type: string }> })
    .allowCredentials
  if (Array.isArray(allowCredentials)) {
    prepared.allowCredentials = allowCredentials.map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    }))
  }
  return prepared as unknown as PublicKeyCredentialRequestOptions
}

function buildAssertionResult(credential: PublicKeyCredential) {
  const resp = credential.response as AuthenticatorAssertionResponse
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bufferToBase64url(resp.authenticatorData),
      clientDataJSON: bufferToBase64url(resp.clientDataJSON),
      signature: bufferToBase64url(resp.signature),
      userHandle: resp.userHandle ? bufferToBase64url(resp.userHandle) : null,
    },
  }
}

export class SecureVerificationService {
  /**
   * 检查可用的验证方式
   */
  static async checkAvailableVerificationMethods(): Promise<VerificationMethods> {
    try {
      const [twoFA, passkey] = await Promise.all([
        get2FAStatusSimpleApi().catch(() => ({ enabled: false })),
        getPasskeyStatusSimpleApi().catch(() => ({ enabled: false })),
      ])
      return {
        has2FA: !!twoFA?.enabled,
        hasPasskey: !!passkey?.enabled,
        passkeySupported: isPasskeySupported(),
      }
    } catch {
      return { has2FA: false, hasPasskey: false, passkeySupported: false }
    }
  }

  static async verify2FA(code: string): Promise<void> {
    if (!code?.trim()) throw new Error('请输入验证码或备用码')
    await verifyApi({ method: '2fa', code: code.trim() })
  }

  static async verifyPasskey(): Promise<void> {
    if (!isPasskeySupported()) throw new Error('当前浏览器不支持 Passkey')
    const begin = (await passkeyVerifyBeginApi()) as { options: Record<string, unknown> }
    const publicKey = prepareCredentialRequestOptions(begin.options)
    const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null
    if (!credential) throw new Error('Passkey 验证被取消')
    const payload = buildAssertionResult(credential)
    await passkeyVerifyFinishApi(payload)
    await verifyApi({ method: 'passkey' })
  }

  static async verify(method: '2fa' | 'passkey', code = ''): Promise<void> {
    if (method === '2fa') return this.verify2FA(code)
    if (method === 'passkey') return this.verifyPasskey()
    throw new Error(`不支持的验证方式：${method}`)
  }
}
